import CohortService from '@/services/cohort-service';
import { StandardResponse } from '@/types/common';
import {
  Cohort,
  CreateCohortPayload,
  UpdateCohortPayload,
} from '@/types/cohort';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { getErrorMessage } from '@/utils/common';

export const cohortsKeys = {
  all: ['cohorts'] as const,
  lists: () => [...cohortsKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown> = {}) =>
    [...cohortsKeys.lists(), filters] as const,
  my: () => [...cohortsKeys.all, 'my'] as const,
};

const getCohortErrorMessage = (error: unknown): string => {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;

  if (data) {
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.error === 'string') return data.error;

    for (const value of Object.values(data)) {
      if (Array.isArray(value) && typeof value[0] === 'string') {
        return value[0];
      }
    }
  }

  return getErrorMessage(error);
};

export const sortCohortsByIdDesc = (cohorts: Cohort[] = []): Cohort[] =>
  [...cohorts].sort((a, b) => b.id - a.id);

export const useCohorts = (
  filters?: Record<string, unknown>,
  options?: { enabled?: boolean }
) => {
  return useQuery<StandardResponse<Cohort>>({
    queryKey: cohortsKeys.list(filters ?? {}),
    queryFn: async () => {
      const data = await CohortService.getCohorts(filters);

      return {
        ...data,
        results: sortCohortsByIdDesc(data.results ?? []),
      };
    },
    enabled: options?.enabled ?? true,
  });
};

export const useTenantCohortsForVariables = (options?: {
  enabled?: boolean;
}) => useCohorts(undefined, options);

export const useMyCohorts = (options?: { enabled?: boolean }) => {
  return useQuery<StandardResponse<Cohort>>({
    queryKey: cohortsKeys.my(),
    queryFn: async () => {
      const data = await CohortService.getMyCohorts();

      return {
        ...data,
        results: sortCohortsByIdDesc(data.results ?? []),
      };
    },
    enabled: options?.enabled ?? true,
  });
};

interface UseCohortMutationOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useCreateOrUpdateCohort = ({
  onSuccess,
  successMessage,
}: UseCohortMutationOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id?: number;
      data: CreateCohortPayload | UpdateCohortPayload;
    }) => {
      if (id) {
        return CohortService.updateCohort(id, data as UpdateCohortPayload);
      }
      return CohortService.createCohort(data as CreateCohortPayload);
    },
    onSuccess: () => {
      toast.success(successMessage ?? 'Cohort saved successfully');
      queryClient.invalidateQueries({ queryKey: cohortsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getCohortErrorMessage(error) || 'Failed to save cohort');
    },
  });
};

export const useDeleteCohort = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>({
    mutationFn: (id: number) => CohortService.deleteCohort(id),
    onSuccess: () => {
      toast.success('Cohort deleted successfully');
      queryClient.invalidateQueries({ queryKey: cohortsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: unknown) => {
      toast.error(getCohortErrorMessage(error) || 'Failed to delete cohort');
    },
  });
};
